import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../api/axios';
import Navbar from './Navbar';
import Footer from './Footer';

const ROLE_LABELS = {
  job_seeker: 'Job Seeker',
  employer: 'Employer',
  recruiter: 'Recruiter',
};

const READONLY_CLS =
  'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-600 cursor-not-allowed';

function RequestAccountDeletion() {
  const navigate = useNavigate();
  const [user] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  const [account, setAccount] = useState(null);
  const [existing, setExisting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem('token')) {
      navigate('/login');
      return;
    }

    const load = async () => {
      try {
        const [profileRes, reqRes] = await Promise.all([
          api.get('/profile'),
          api.get('/account-deletion-request'),
        ]);
        setAccount(profileRes.data);
        setExisting(reqRes.data?.request || null);
      } catch (err) {
        setLoadError(err.response?.data?.message || 'Could not load your account details.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (reason.trim().length < 5) {
      setError('Please tell us the reason (at least 5 characters).');
      return;
    }
    if (!confirmed) {
      setError('Please tick the box to confirm.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await api.post('/account-deletion-request', { reason: reason.trim() });
      setDone(true);
    } catch (err) {
      const errors = err.response?.data?.errors;
      setError(
        errors
          ? Object.values(errors).flat().join(' ')
          : err.response?.data?.message || 'Could not send your request. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const pending = existing && existing.status === 'pending';
  const rejected = existing && existing.status === 'rejected';

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAF5FF] to-[#FDF2F8]">
      <Navbar user={user} />

      <div className="max-w-xl mx-auto px-3 sm:px-4 py-8 sm:py-10">
        <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#7C3AED] via-[#C026D3] to-[#EC4899] bg-clip-text text-transparent inline-block mb-4">
          Delete My Account
        </h2>

        {loading ? (
          <div className="bg-white rounded-xl shadow p-8 text-center text-gray-400">Loading...</div>
        ) : loadError ? (
          <div className="bg-white rounded-xl shadow p-6 text-center text-red-600 text-sm">{loadError}</div>
        ) : done ? (
          <div className="bg-white rounded-xl shadow p-6 text-center space-y-3">
            <p className="text-lg font-bold text-[#10B981]">Request sent</p>
            <p className="text-sm text-gray-600">
              Our team will review your request and delete your account. You can keep using your
              account until then.
            </p>
            <Link to="/dashboard" className="inline-block text-sm font-semibold text-[#7C3AED] hover:underline">
              Back to dashboard
            </Link>
          </div>
        ) : pending ? (
          <div className="bg-white rounded-xl shadow p-6 space-y-2">
            <p className="font-bold text-[#F59E0B]">Your request is waiting for review</p>
            <p className="text-sm text-gray-600">
              Sent on {new Date(existing.created_at).toLocaleDateString('en-IN')}. We will contact you
              if we need anything else.
            </p>
            <p className="text-sm text-gray-500 whitespace-pre-wrap break-words">
              <span className="text-gray-400">Your reason:</span> {existing.reason}
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow p-4 sm:p-6 space-y-4">
            {rejected && (
              <div className="bg-amber-50 border border-amber-100 text-amber-700 text-sm rounded-lg p-3">
                Your earlier request was not approved
                {existing.admin_note ? `: ${existing.admin_note}` : '.'} You can send a new request below.
              </div>
            )}

            <div className="bg-red-50 border border-red-100 text-red-700 text-sm rounded-lg p-3">
              Deleting your account is permanent. Your profile, resume details, applications and saved
              jobs will be removed and cannot be restored.
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Name</label>
                <input type="text" value={account?.name || ''} readOnly className={READONLY_CLS} />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Account type</label>
                <input type="text" value={ROLE_LABELS[account?.role] || account?.role || ''} readOnly className={READONLY_CLS} />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Email</label>
                <input type="text" value={account?.email || ''} readOnly className={READONLY_CLS} />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Mobile number</label>
                <input type="text" value={account?.phone || ''} readOnly className={READONLY_CLS} />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                Reason for deleting your account
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={4}
                maxLength={1000}
                placeholder="Please tell us why you want to delete your account"
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#9333EA] focus:border-transparent"
              />
              <p className="text-xs text-gray-400 mt-1 text-right">{reason.length}/1000</p>
            </div>

            <label className="flex items-start gap-2.5 text-sm text-gray-600 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-[#7C3AED] shrink-0"
              />
              <span>I understand that my account and data will be permanently deleted.</span>
            </label>

            {error && <p className="text-sm text-red-600">{error}</p>}

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <Link
                to="/profile"
                className="text-center px-5 py-2 rounded-lg text-sm font-semibold text-gray-500 hover:bg-gray-100"
              >
                Cancel
              </Link>
              <button
                type="submit"
                disabled={submitting}
                className="bg-gradient-to-r from-[#DC2626] to-[#DB2777] text-white px-6 py-2 rounded-lg text-sm font-semibold hover:opacity-90 disabled:opacity-50"
              >
                {submitting ? 'Sending…' : 'Send deletion request'}
              </button>
            </div>
          </form>
        )}
      </div>

      <Footer />
    </div>
  );
}

export default RequestAccountDeletion;